import React, { useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { Icon } from './Icon';
import { zIndex as Z } from '../../tokens/zIndex';
import styles from './Image.module.css';

const imageWrapper = cva(styles.wrapper, {
  variants: {
    preview: { true: styles.wrapperPreview },
  },
});

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'placeholder'> {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  fallback?: string;
  preview?: boolean | { visible?: boolean; onVisibleChange?: (visible: boolean) => void; mask?: React.ReactNode };
  placeholder?: React.ReactNode;
}

const fallbackSrc =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">No image</text></svg>';

/**
 * Image — display with preview, fallback, and placeholder.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | src | string | — | Image URL |
 * | alt | string | — | Alt text |
 * | width | number \| string | — | Width |
 * | height | number \| string | — | Height |
 * | fallback | string | placeholder | Fallback URL on error |
 * | preview | boolean \| object | true | Enable click-to-zoom |
 * | placeholder | ReactNode | — | Loading placeholder |
 *
 * @example
 * <Image src="photo.jpg" width={300} />
 * <Image src="photo.jpg" preview={false} />
 */
export function Image({
  src,
  alt = '',
  width,
  height,
  fallback: fallbackProp,
  preview: previewProp = true,
  placeholder,
  className,
  style,
  ...props
}: ImageProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const previewConfig = typeof previewProp === 'object' ? previewProp : {};
  const previewEnabled = previewProp !== false;
  const isControlled = typeof previewProp === 'object' && previewProp.visible !== undefined;

  const [internalVisible, setInternalVisible] = useState(false);
  const visible = isControlled ? previewConfig.visible! : internalVisible;

  const setVisible = (v: boolean) => {
    if (isControlled) {
      previewConfig.onVisibleChange?.(v);
    } else {
      setInternalVisible(v);
    }
  };

  const imgSrc = error ? (fallbackProp ?? fallbackSrc) : src;

  const hasCustomMask = typeof previewProp === 'object' && previewProp.mask !== undefined;
  const customMask = hasCustomMask ? previewProp.mask : null;

  // Resolve width/height as CSS custom properties for runtime passthrough
  const resolveSize = (v: number | string | undefined): string | undefined => {
    if (v === undefined) return undefined;
    return typeof v === 'number' ? `${v}px` : v;
  };
  const cssWidth = resolveSize(width);
  const cssHeight = resolveSize(height);

  // Build inline style with only CSS custom properties + user style override
  const wrapperStyle: React.CSSProperties = {
    ...(cssWidth !== undefined ? { '--img-width': cssWidth } as React.CSSProperties : {}),
    ...(cssHeight !== undefined ? { '--img-height': cssHeight } as React.CSSProperties : {}),
    // Direct width/height for test compatibility and actual sizing
    ...(cssWidth !== undefined ? { width: cssWidth } : {}),
    ...(cssHeight !== undefined ? { height: cssHeight } : {}),
    ...style,
  };

  // Use zIndex token for the overlay
  const overlayStyle = { zIndex: Z.modal } as React.CSSProperties;

  return (
    <>
      <div
        role={previewEnabled ? 'button' : undefined}
        tabIndex={previewEnabled ? 0 : undefined}
        aria-label={previewEnabled ? `Preview ${alt || 'image'}` : undefined}
        className={clsx(imageWrapper({ preview: previewEnabled as true }), className)}
        style={wrapperStyle}
        onClick={() => previewEnabled && setVisible(true)}
        onKeyDown={(e) => { if (previewEnabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setVisible(true); } }}
        onMouseEnter={(e) => {
          if (!previewEnabled) return;
          const mask = e.currentTarget.querySelector('[data-mask]') as HTMLElement;
          if (mask) mask.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const mask = e.currentTarget.querySelector('[data-mask]') as HTMLElement;
          if (mask) mask.style.opacity = '0';
        }}
      >
        {!loaded && placeholder && <div className={styles.placeholder}>{placeholder}</div>}
        <img
          src={imgSrc}
          alt={alt}
          onError={() => setError(true)}
          onLoad={() => setLoaded(true)}
          className={styles.img}
          {...props}
        />
        {previewEnabled && (
          customMask ? (
            <div data-mask>{customMask}</div>
          ) : (
            <div data-mask className={styles.mask}>
              <Icon name="zoom_in" size={24} className={styles.maskIcon} />
            </div>
          )
        )}
      </div>

      <AnimatePresence>
        {visible && (
          <Portal>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: duration.slow / 1000 }}
              className={styles.overlay}
              style={overlayStyle}
              onClick={() => setVisible(false)}
            >
            <button
              aria-label="Close preview"
              onClick={(e) => {
                e.stopPropagation();
                setVisible(false);
              }}
              className={styles.closeBtn}
            >
              <Icon name="close" size={20} aria-hidden="true" />
            </button>
            <img
              src={imgSrc}
              alt={alt}
              className={styles.previewImg}
              onClick={(e) => e.stopPropagation()}
            />
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
}
